// I/O schemas for the M3 admin email surface (motiflink §4.5).

import { z } from 'zod';

const languageCode = z
  .string()
  .min(2)
  .max(10)
  .regex(/^[a-z][a-z0-9-]*$/, 'lowercase letters, digits and dashes only');

export const emailSendStatus = z.enum(['sent', 'failed', 'suppressed']);
export const suppressionReason = z.enum(['bounced', 'unsubscribed', 'complaint', 'manual']);

// `slug` is intentionally absent: it is the identifier code sends against, so
// renaming one would silently break a live send path (motiflink §4.5).
export const updateEmailTemplateInput = z
  .object({
    name: z.string().min(1).max(120),
    subject: z.string().min(1).max(400),
    htmlBody: z.string().max(200_000),
    isActive: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });

export const listEmailTemplatesQuery = z.object({
  lang: languageCode.optional(),
});

export const getEmailTemplateQuery = z.object({
  lang: languageCode.default('en'),
});

export const listEmailLogQuery = z.object({
  status: emailSendStatus.optional(),
  template: z.string().max(120).optional(),
  email: z.string().max(320).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const addSuppressedEmailInput = z.object({
  email: z.string().email().max(320),
  // Admin additions are 'manual' by definition; provider events come through
  // the webhook, not this endpoint.
  reason: suppressionReason.default('manual'),
});

export const listSuppressedQuery = z.object({
  q: z.string().max(320).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const sendTestEmailInput = z.object({
  templateSlug: z.string().min(1).max(120),
  to: z.string().email().max(320),
  lang: languageCode.default('en'),
});

export const updateEmailSettingsInput = z
  .object({
    companyName: z.string().min(1).max(160),
    logoUrl: z.string().url().max(1000).nullable(),
    tagline: z.string().max(300).nullable(),
    websiteUrl: z.string().url().max(1000).nullable(),
    contactEmail: z.string().email().max(320).nullable(),
    footerText: z.string().max(1000).nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'no fields to update' });

export type UpdateEmailTemplateInput = z.infer<typeof updateEmailTemplateInput>;
export type ListEmailLogQuery = z.infer<typeof listEmailLogQuery>;
export type SendTestEmailInput = z.infer<typeof sendTestEmailInput>;
export type UpdateEmailSettingsInput = z.infer<typeof updateEmailSettingsInput>;
